import { createSlice } from '@reduxjs/toolkit';
import { includes, get } from 'lodash';
import Cookies from 'universal-cookie';
import { API } from 'store/middleware/api';
import {
	getExperimentTreatment,
	logExperimentImpression,
	isExperimentActive,
} from 'utils/experimentUtils';

export const initialState = {
	bucket: {},
	forcedBucket: {},
	definitions: [],
	loggedImpressions: [],
	loading: false,
	loaded: false,
	error: null,
};

const experimentSlice = createSlice({
	name: 'experiments',
	initialState,
	reducers: {
		setExperimentsBucket: (state, action) => ({
			...state,
			bucket: { ...action.payload },
		}),
		clearExperimentsBucket: (state) => ({
			...state,
			loading: true,
			bucket: initialState.bucket,
			forcedBucket: initialState.forcedBucket,
		}),
		bucketingLogged: (state, action) => state,
		impressionLogged: {
			reducer: (state, action) => ({
				...state,
				loggedImpressions: [
					...state.loggedImpressions,
					action.payload.experimentName,
				],
			}),
			prepare: (experimentName, variationName) => ({
				payload: { experimentName, variationName },
			}),
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase('SFDC/EXPERIMENT/INIT_EXPERIMENTS_REQUEST', (state) => ({
				...state,
				loading: true,
			}))
			.addCase('SFDC/EXPERIMENT/INIT_EXPERIMENTS_SUCCESS', (state) => ({
				...state,
				loading: false,
			}))
			.addCase('SFDC/EXPERIMENT/INIT_EXPERIMENTS_FAILURE', (state) => ({
				...state,
				loading: false,
				error: true,
			}))
			.addCase('SFDC/EXPERIMENT/GET_EXPERIMENTS_DEFINITIONS_REQUEST', (state) => ({
				...state,
				loading: true,
			}))
			.addCase('SFDC/EXPERIMENT/GET_EXPERIMENTS_DEFINITIONS_SUCCESS', (state, action) => ({
				...state,
				definitions: get(action, 'payload.definitions', []),
				loading: false,
				loaded: true,
			}))
			.addCase('SFDC/EXPERIMENT/GET_EXPERIMENTS_DEFINITIONS_FAILURE', (state) => ({
				...state,
				loading: false,
				loaded: false,
				error: true,
			}));
	},
});

export const {
	setExperimentsBucket,
	clearExperimentsBucket,
	bucketingLogged,
	impressionLogged,
} = experimentSlice.actions;

export const EXPERIMENT = {
	SET_EXPERIMENTS_BUCKET: setExperimentsBucket.type,
	CLEAR_EXPERIMENTS_BUCKET: clearExperimentsBucket.type,
	IMPRESSION_LOGGED: impressionLogged.type,
	BUCKETING_LOGGED: bucketingLogged.type,
	GET_EXPERIMENTS_DEFINITIONS_REQUEST: 'SFDC/EXPERIMENT/GET_EXPERIMENTS_DEFINITIONS_REQUEST',
	GET_EXPERIMENTS_DEFINITIONS_SUCCESS: 'SFDC/EXPERIMENT/GET_EXPERIMENTS_DEFINITIONS_SUCCESS',
	GET_EXPERIMENTS_DEFINITIONS_FAILURE: 'SFDC/EXPERIMENT/GET_EXPERIMENTS_DEFINITIONS_FAILURE',
	INIT_EXPERIMENTS_REQUEST: 'SFDC/EXPERIMENT/INIT_EXPERIMENTS_REQUEST',
	INIT_EXPERIMENTS_SUCCESS: 'SFDC/EXPERIMENT/INIT_EXPERIMENTS_SUCCESS',
	INIT_EXPERIMENTS_FAILURE: 'SFDC/EXPERIMENT/INIT_EXPERIMENTS_FAILURE',
};

export function getExperimentsDefinitions() {
	return {
		[API]: {
			types: [
				EXPERIMENT.GET_EXPERIMENTS_DEFINITIONS_REQUEST,
				EXPERIMENT.GET_EXPERIMENTS_DEFINITIONS_SUCCESS,
				EXPERIMENT.GET_EXPERIMENTS_DEFINITIONS_FAILURE,
			],
			method: 'GET',
			endpoint: '/experiments/definitions',
		},
	};
}

export function initExperiments(data) {
	return {
		[API]: {
			types: [
				EXPERIMENT.INIT_EXPERIMENTS_REQUEST,
				EXPERIMENT.INIT_EXPERIMENTS_SUCCESS,
				EXPERIMENT.INIT_EXPERIMENTS_FAILURE,
			],
			method: 'POST',
			endpoint: '/experiments/init',
			data,
		},
	};
}

export const logBucketing = () => (dispatch, getState) => {
	const { experiments } = getState();
	dispatch(bucketingLogged(experiments.bucket));
};

export const logImpression = (experimentName) => (dispatch, getState) => {
	const { experiments } = getState();
	if (!includes(experiments.loggedImpressions, experimentName)) {
		const variationName = getExperimentTreatment(experimentName, experiments.bucket);
		if (isExperimentActive(experimentName, experiments.bucket)) {
			logExperimentImpression(experimentName, variationName);
			dispatch(impressionLogged(experimentName, variationName));
		}
	}
};

export const clearForcedExperiments = () => (dispatch) => {
	const cookies = new Cookies();
	cookies.remove('visit', { path: '/' });
	cookies.remove('visitor', { path: '/' });
	cookies.remove('visitor_id', { path: '/' });
	cookies.remove('sf_forced_experiments', { path: '/' });
	dispatch(clearExperimentsBucket());
};

export const EXPERIMENT_STORE_NAME = 'experiments';

export default experimentSlice.reducer;
